import React, { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'pdfjs-dist/webpack.mjs';

const CommonPdfViewer = ({
  fileUrl,
  onDocumentLoad,
  onDocumentLoadError,
  defaultScale = 1.1,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [loadedPages, setLoadedPages] = useState({});

  const observerRef = useRef(null);

  useEffect(() => {
    setNumPages(0);
    setLoadedPages({});

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, [fileUrl]);

  const handleLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);

    // IMPORTANT: don't load every page here
    onDocumentLoad?.({
      doc: pdf,
    });
  };

  const handleLoadError = (error) => {
    setNumPages(0);
    setLoadedPages({});

    onDocumentLoadError?.(error);
  };

  const observePage = (element, pageNumber) => {
    if (!element) {
      return;
    }

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const page = Number(entry.target.dataset.page);

              setLoadedPages((prev) => ({
                ...prev,
                [page]: true,
              }));
            }
          });
        },
        {
          root: null,
          rootMargin: '800px 0px',
          threshold: 0,
        }
      );
    }

    element.dataset.page = pageNumber;
    observerRef.current.observe(element);
  };

  if (!fileUrl) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Document
        file={fileUrl}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        loading={null}
        error={null}
      >
        {Array.from({ length: numPages }, (_, index) => {
          const pageNumber = index + 1;

          return (
            <div
              key={`${fileUrl}-page-wrapper-${pageNumber}`}
              ref={(element) => observePage(element, pageNumber)}
              style={{
                width: '100%',
                minHeight: '800px',
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '20px',
              }}
            >
              {loadedPages[pageNumber] && (
                <Page
                  pageNumber={pageNumber}
                  scale={defaultScale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={null}
                />
              )}
            </div>
          );
        })}
      </Document>
    </div>
  );
};

export default CommonPdfViewer;
